'use client';

import { useState, useCallback, useEffect } from 'react';
import { ArrowLeft, FileText, ListChecks, GitCompare, ChevronRight } from 'lucide-react';
import { Project, Artifact, artifactContent } from './mockData';
import { ArtifactsTab } from './ArtifactsTab';
import { IssuesTab } from './IssuesTab';
import { ChangesTab } from './ChangesTab';
import { ArtifactEditor } from './viewer/ArtifactEditor';
import { UnsavedChangesDialog } from './viewer/UnsavedChangesDialog';
import { ViewerAnnotation } from './viewer/types';
import { loadAnnotations, saveAnnotations, hasUnsavedChanges as checkUnsavedChanges } from './viewer/annotationStorage';

type Tab = 'artifacts' | 'issues' | 'changes' | 'editor';
type Stage = 'specify' | 'plan' | 'tasks' | null;

interface SpecViewProps {
  project: Project;
  onBack: () => void;
}

export function SpecView({ project, onBack }: SpecViewProps) {
  const [activeTab, setActiveTab] = useState<Tab>('artifacts');
  const [selectedStage, setSelectedStage] = useState<Stage>(null);

  // Editor state
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(null);
  const [annotations, setAnnotations] = useState<ViewerAnnotation[]>([]);
  const [savedAnnotations, setSavedAnnotations] = useState<ViewerAnnotation[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Dialog state for unsaved changes warning
  const [pendingNavigation, setPendingNavigation] = useState<{ type: 'tab'; tab: Tab } | { type: 'back' } | null>(null);

  const tabs = [
    { key: 'artifacts', label: 'Artifacts', icon: <FileText className="w-4 h-4" /> },
    { key: 'issues', label: 'Issues', icon: <ListChecks className="w-4 h-4" /> },
    { key: 'changes', label: 'Changes', icon: <GitCompare className="w-4 h-4" /> },
  ] as const;

  const handleStageClick = (stage: Stage) => {
    setSelectedStage(selectedStage === stage ? null : stage);
    // Switch to artifacts tab when clicking a stage
    if (activeTab !== 'artifacts' && activeTab !== 'editor') {
      setActiveTab('artifacts');
    }
  };

  // Handle artifact click - open editor
  const handleArtifactClick = useCallback((artifact: Artifact) => {
    setSelectedArtifact(artifact);
    setActiveTab('editor');
    // Load annotations from storage (or demo annotations on first load)
    const loaded = loadAnnotations(artifact.name);
    setAnnotations(loaded);
    setSavedAnnotations(loaded);
    setHasUnsavedChanges(false);
  }, []);

  // Handle tab navigation with unsaved changes check
  const handleTabClick = useCallback((tab: Tab) => {
    if (activeTab === 'editor' && hasUnsavedChanges) {
      setPendingNavigation({ type: 'tab', tab });
    } else {
      setActiveTab(tab);
      if (tab !== 'editor') {
        setSelectedArtifact(null);
      }
    }
  }, [activeTab, hasUnsavedChanges]);

  // Handle back button with unsaved changes check
  const handleBack = useCallback(() => {
    if (activeTab === 'editor' && hasUnsavedChanges) {
      setPendingNavigation({ type: 'back' });
    } else {
      onBack();
    }
  }, [activeTab, hasUnsavedChanges, onBack]);

  // Handle close editor
  const handleCloseEditor = useCallback(() => {
    if (hasUnsavedChanges) {
      setPendingNavigation({ type: 'tab', tab: 'artifacts' });
    } else {
      setActiveTab('artifacts');
      setSelectedArtifact(null);
    }
  }, [hasUnsavedChanges]);

  // Add annotation
  const handleAddAnnotation = useCallback((annotation: Omit<ViewerAnnotation, 'id' | 'createdAt'>) => {
    const newAnnotation: ViewerAnnotation = {
      ...annotation,
      id: `ann-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      createdAt: Date.now(),
    };
    setAnnotations(prev => {
      const updated = [...prev, newAnnotation];
      // Check if different from saved state
      if (selectedArtifact) {
        setHasUnsavedChanges(checkUnsavedChanges(selectedArtifact.name, updated));
      }
      return updated;
    });
  }, [selectedArtifact]);

  // Delete annotation
  const handleDeleteAnnotation = useCallback((id: string) => {
    setAnnotations(prev => {
      const updated = prev.filter(a => a.id !== id);
      // Check if different from saved state
      if (selectedArtifact) {
        setHasUnsavedChanges(checkUnsavedChanges(selectedArtifact.name, updated));
      }
      return updated;
    });
  }, [selectedArtifact]);

  // Save annotations
  const handleSave = useCallback(() => {
    if (selectedArtifact) {
      saveAnnotations(selectedArtifact.name, annotations);
    }
    setSavedAnnotations(annotations);
    setHasUnsavedChanges(false);
  }, [annotations, selectedArtifact]);

  // Dialog actions
  const handleDialogSave = useCallback(() => {
    handleSave();
    if (pendingNavigation) {
      if (pendingNavigation.type === 'tab') {
        setActiveTab(pendingNavigation.tab);
        if (pendingNavigation.tab !== 'editor') {
          setSelectedArtifact(null);
        }
      } else {
        onBack();
      }
    }
    setPendingNavigation(null);
  }, [handleSave, pendingNavigation, onBack]);

  const handleDialogDiscard = useCallback(() => {
    setAnnotations(savedAnnotations);
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      if (pendingNavigation.type === 'tab') {
        setActiveTab(pendingNavigation.tab);
        if (pendingNavigation.tab !== 'editor') {
          setSelectedArtifact(null);
        }
      } else {
        onBack();
      }
    }
    setPendingNavigation(null);
  }, [savedAnnotations, pendingNavigation, onBack]);

  const handleDialogCancel = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  // Get content for selected artifact
  const getArtifactContent = () => {
    if (!selectedArtifact) return '';
    return artifactContent[selectedArtifact.name] || `# ${selectedArtifact.name}\n\nNo content available for this artifact.`;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border/30 shrink-0">
        <div className="flex items-center gap-4 mb-3">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 text-text-muted hover:text-text transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>

          {/* Breadcrumb */}
          <div className="text-text-muted text-sm">
            <span className="hover:text-text cursor-pointer">{project.name}</span>
            <span className="mx-2">/</span>
            <span className="text-text">{project.activeSpec}</span>
          </div>
        </div>

        {/* Title Row with Phase Breadcrumbs */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-text-muted" />
            <h1 className="text-xl font-semibold text-text">
              {formatSpecName(project.activeSpec || '')}
            </h1>
          </div>

          {/* Phase Breadcrumbs */}
          <PhaseBreadcrumbs
            phases={project.phases}
            selectedStage={selectedStage}
            onStageClick={handleStageClick}
          />
        </div>
      </div>

      {/* Tab Navigation - hidden when in editor view */}
      {activeTab !== 'editor' && (
        <div className="px-6 border-b border-border/30 shrink-0">
          <div className="flex items-center gap-1 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabClick(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'artifacts' && (
          <div className="h-full overflow-y-auto">
            <ArtifactsTab
              phases={project.phases}
              selectedStage={selectedStage}
              onArtifactClick={handleArtifactClick}
            />
          </div>
        )}
        {activeTab === 'issues' && (
          <div className="h-full overflow-y-auto">
            <IssuesTab />
          </div>
        )}
        {activeTab === 'changes' && (
          <div className="h-full overflow-y-auto">
            <ChangesTab />
          </div>
        )}
        {activeTab === 'editor' && selectedArtifact && (
          <ArtifactEditor
            artifact={selectedArtifact}
            content={getArtifactContent()}
            annotations={annotations}
            hasUnsavedChanges={hasUnsavedChanges}
            onAddAnnotation={handleAddAnnotation}
            onDeleteAnnotation={handleDeleteAnnotation}
            onSave={handleSave}
            onClose={handleCloseEditor}
          />
        )}
      </div>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={pendingNavigation !== null}
        onSave={handleDialogSave}
        onDiscard={handleDialogDiscard}
        onCancel={handleDialogCancel}
      />
    </div>
  );
}

// Phase breadcrumbs component
interface PhaseBreadcrumbsProps {
  phases: Project['phases'];
  selectedStage: Stage;
  onStageClick: (stage: Stage) => void;
}

function PhaseBreadcrumbs({ phases, selectedStage, onStageClick }: PhaseBreadcrumbsProps) {
  // Strong color progression: light → medium → dark (all vibrant)
  const phaseConfig = [
    {
      key: 'specify' as const,
      label: 'Specify',
      number: 1,
      bgActive: 'bg-fuchsia-400',
      bgInactive: 'bg-gray-500',
    },
    {
      key: 'plan' as const,
      label: 'Plan',
      number: 2,
      bgActive: 'bg-violet-500',
      bgInactive: 'bg-gray-500',
    },
    {
      key: 'tasks' as const,
      label: 'Tasks',
      number: 3,
      bgActive: 'bg-indigo-600',
      bgInactive: 'bg-gray-500',
    },
  ];

  // Determine current active phase
  const getPhaseState = (key: 'specify' | 'plan' | 'tasks') => {
    const status = phases[key];
    if (status === 'complete') return 'complete';
    if (status === 'in-progress') return 'active';
    return 'inactive';
  };

  return (
    <div className="flex items-center gap-1">
      {phaseConfig.map((phase, index) => {
        const state = getPhaseState(phase.key);
        const isActive = state === 'active' || state === 'complete';
        const isSelected = selectedStage === phase.key;

        return (
          <div key={phase.key} className="flex items-center">
            {/* Phase Item - Clickable */}
            <button
              onClick={() => onStageClick(phase.key)}
              className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-all ${
                isSelected
                  ? 'bg-white/10 ring-2 ring-primary/50'
                  : 'hover:bg-white/5'
              } ${isActive ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
              disabled={!isActive}
            >
              {/* Numbered Badge */}
              <span
                className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full text-white shadow-md ${
                  isActive ? phase.bgActive : phase.bgInactive
                }`}
              >
                {phase.number}
              </span>

              {/* Label */}
              <span
                className={`text-sm font-medium ${
                  isActive ? 'text-text' : 'text-text-muted'
                }`}
              >
                {phase.label}
              </span>
            </button>

            {/* Chevron Separator */}
            {index < phaseConfig.length - 1 && (
              <ChevronRight className="w-4 h-4 text-text-muted mx-1" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// Helper to format spec ID into readable name
function formatSpecName(specId: string): string {
  // e.g., "001-authentication" -> "Authentication"
  const name = specId.replace(/^\d+-/, '').replace(/-/g, ' ');
  return name.charAt(0).toUpperCase() + name.slice(1);
}
